<template>
  <div>
    <div :class="$style.tabs" role="tablist">
      <aw-red-tab
        v-for="tab in tabs"
        :id="`red-tab-${tab.contentId}`"
        :key="tab.name"
        :tab="tab"
        :selected="tab.contentId === currentSelectedTab"
        role="tab"
        :aria-selected="(tab.contentId === currentSelectedTab).toString()"
        :aria-controls="`red-tab-content-${tab.contentId}`"
        @select="selectTab"
      />
    </div>
    <div :class="[$style.content, $style[`content${currentTabIndex}`]]" :style="{'--awRedTabsTabNumber': tabs.length}">
      <Transition
        tag="div"
        mode="out-in"
        :name="$style.contentTransition"
        :enter-active-class="$style.contentTransitionEnterActive"
        :enter-from-class="$style.contentTransitionEnter"
        :leave-active-class="$style.contentTransitionLeaveActive"
        :leave-to-class="$style.contentTransitionLeaveTo"
      >
        <div
          :id="`red-tab-content-${currentSelectedTab}`"
          :key="currentSelectedTab"
          role="tabpanel"
          :aria-labelledby="`red-tab-${currentSelectedTab}`"
          :class="$style.contentText"
        >
          <slot :content="currentTabContent" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue';
  import AwRedTab from '~~/common/components/Common/AwRedTabs/AwRedTab.vue';

  const props = defineProps({
    tabs: {
      type: Array,
      required: true,
    },
  });

  const emit = defineEmits(['select-tab']);

  const tabs = computed(() => Array.isArray(props.tabs) ? props.tabs : []);
  const currentSelectedTab = ref(tabs.value[0]?.contentId);
  const currentTabIndex = computed(() => {
    const val = currentSelectedTab.value;
    return tabs.value.findIndex((tab) => tab.contentId === val);
  });
  const currentTabContent = computed(() => {
    return tabs.value[currentTabIndex.value]?.content;
  });

  onMounted(() => {
    selectTab(tabs.value[0]?.contentId);
  });

  function selectTab (contentId) {
    if (currentSelectedTab.value !== contentId) {
      currentSelectedTab.value = contentId;
    }
  }

  watch(currentTabIndex, () => {
    emit('select-tab', currentTabIndex.value);
  });
</script>

<style module lang="scss" rel="stylesheet/scss">
.tabs {
  display: flex;
  flex-direction: row;
  min-height: 100px;
  margin-top: 16px;
  margin-bottom: 36px;
  gap: 4px;
  animation: fadeUp $animation-speed-medium ease-in-out;

  @include tablet(min) {
    gap: 20px;
  }
  @include desktop-small(min) {
    margin-bottom: 56px;
    gap: 40px;
  }
}

.content {
  position: relative;
  padding: 20px 16px;
  animation: fadeDown $animation-speed-medium ease-in-out;
  border-radius: 16px;
  background-color: $color-background-2;
  @include tablet(min) {
    padding: 40px;
    border-radius: 24px;
  }

  &Transition {
    &EnterActive,
    &LeaveActive {
      transition: opacity $animation-speed-fast ease-in-out;
      opacity: 1;
    }

    &Enter,
    &LeaveTo {
      opacity: 0;
    }
  }

  &::before {
    position: absolute;
    top: -16px;
    display: inline-block;
    content: "";
    transition: all $animation-speed-fast $animation-timing-function;
    transform: scale(-1);
    border-width: 16px 12px 0;
    border-style: solid;
    border-color: $color-background-2 transparent;
  }

  $aw-red-tab-gap-mobile: 4px;
  $aw-red-tab-gap-tablet: 20px;
  $aw-red-tab-gap-desktop: 40px;

  $aw-red-tab-gap-number: calc(var(--awRedTabsTabNumber) - 1);

  $aw-red-tab-width-mobile: calc((100% - $aw-red-tab-gap-mobile * $aw-red-tab-gap-number) / var(--awRedTabsTabNumber));
  $aw-red-tab-width-tablet: calc((100% - $aw-red-tab-gap-tablet * $aw-red-tab-gap-number) / var(--awRedTabsTabNumber));
  $aw-red-tab-width-desktop: calc((100% - $aw-red-tab-gap-desktop * $aw-red-tab-gap-number) / var(--awRedTabsTabNumber));

  $aw-red-tab-pointer-size: 8px;

  &:not(&0)::before {
    left: calc($aw-red-tab-width-mobile * (var(--awRedTabsMultiplicationFactor) + 0.5) + $aw-red-tab-gap-mobile * var(--awRedTabsMultiplicationFactor) - $aw-red-tab-pointer-size);
    @include tablet(min) {
      left: calc($aw-red-tab-width-tablet * (var(--awRedTabsMultiplicationFactor) + 0.5) + $aw-red-tab-gap-tablet * var(--awRedTabsMultiplicationFactor) - $aw-red-tab-pointer-size);
    }
    @include desktop-small(min) {
      left: calc($aw-red-tab-width-desktop * (var(--awRedTabsMultiplicationFactor) + 0.5) + $aw-red-tab-gap-desktop * var(--awRedTabsMultiplicationFactor) - $aw-red-tab-pointer-size);
    }
  }

  &0::before {
    left: calc(($aw-red-tab-width-mobile - $aw-red-tab-pointer-size) / 2);
    @include desktop-small(min) {
      left: calc(($aw-red-tab-width-desktop - $aw-red-tab-pointer-size) / 2);
    }
  }

  &1::before {
    --awRedTabsMultiplicationFactor: 1;
  }

  &2::before {
    --awRedTabsMultiplicationFactor: 2;
  }

  &3::before {
    --awRedTabsMultiplicationFactor: 3;
  }
}

@keyframes fadeUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
